import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {validateEndDate} from 'src/modules/profile/account/tabs/license-and-features/report-tab/components/utils/dateValidationUtils';
import {createNotification} from 'src/openapi/admin/apis/InternalNotificationApi';
import {useAppDispatch} from 'src/store/hooks';

import {validateContentSize} from '../content-builder/utils';

import type {CreateNotificationRequest} from 'src/openapi/admin/models/CreateNotificationRequest';
import type {SuccessPayload} from 'src/typedef';

export const useCreateNotification = () => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(() => {
        return Promise.resolve({type: '', payload: {
            success: true,
            error: false,
            data: {
                content: '',
                title: '',
                topic: '',
                startDate: '',
                endDate: '',
                adminProfileIds: [],
            },
        } as SuccessPayload<CreateNotificationRequest>});
    }, []);

    const saveDispatchAction = useCallback((
        createNotificationRequest: CreateNotificationRequest,
        signal: AbortSignal,
    ) => apiDispatch(
        createNotification({createNotificationRequest}, {signal}),
    ), [apiDispatch]);

    const editValidation = useCallback((data: CreateNotificationRequest) => {
        const sizeExceedError = validateContentSize(data.content || '');
        if (sizeExceedError) {
            addError(sizeExceedError);
        } else {
            removeError('notification-content');
        }

        const endDateError = validateEndDate(data.endDate, data.startDate, 'notification-end-date');
        if (endDateError) {
            addError(endDateError);
        } else {
            removeError('notification-end-date');
        }
    }, [addError, removeError]);

    const saveValidation = useCallback((data: CreateNotificationRequest) => {
        let valid = true;
        const sizeExceedError = validateContentSize(data.content || '');
        if (sizeExceedError) {
            valid = false;
            addError(sizeExceedError);
        } else {
            removeError('notification-content');
        }

        const endDateError = validateEndDate(data.endDate, data.startDate, 'notification-end-date');
        if (endDateError) {
            valid = false;
            addError(endDateError);
        } else {
            removeError('notification-end-date');
        }

        return valid;
    }, [addError, removeError]);

    return useCommonDataHook({
        initValue: {
            content: '',
            title: '',
            topic: '',
            startDate: '',
            endDate: '',
            adminProfileIds: [],
        },
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
